"use client";

import { useApolloClient } from "@apollo/client/react";
import { useEffect, useRef } from "react";

/**
 * `Wexio('shutdown')` ends a visitor's session on a possibly shared device. Everything the widget
 * holds IN MEMORY for that visitor (the Apollo cache: threads, history, profile; the live thread
 * overlay; the open route; unread/alert bookkeeping) must go with it, or the next visitor sees it
 * until their own data replaces it. Storage (visitor id, token, drafts, outbox) is cleared by the
 * shutdown handler itself.
 *
 * Runs `reset` (the shell's own state setters) and empties the Apollo cache when `shutdownEpoch`
 * CHANGES; never on mount, and not on a plain re-handshake (retry / renew), which keeps the same
 * visitor. `clearStore` (not `resetStore`) on purpose: nothing may refetch under the old identity;
 * the queries re-run on their own once the new session's token arrives.
 */
export function useSessionMemoryReset(shutdownEpoch: number, reset: () => void): void {
  const client = useApolloClient();
  const resetRef = useRef(reset);
  resetRef.current = reset;
  const last = useRef(shutdownEpoch);
  useEffect(() => {
    if (last.current === shutdownEpoch) return;
    last.current = shutdownEpoch;
    void client.clearStore().catch(() => {});
    resetRef.current();
  }, [shutdownEpoch, client]);
}
